import { Renderer } from '@client/core/Renderer';
import { SaveManager, SaveSlotInfo, getSaveManager } from '@client/save/SaveManager';
import { ClassName } from '@shared/types';
import { t } from '@locales/index';

export type SaveMenuMode = 'save' | 'load';

export class SaveMenuUI {
  private saveManager: SaveManager;
  private isOpen: boolean = false;
  private mode: SaveMenuMode = 'save';
  private screenWidth: number;
  private screenHeight: number;

  private hoveredSlot: number = -1;
  private slots: SaveSlotInfo[] = [];

  // Callbacks
  private onSave: ((slot: number) => void) | null = null;
  private onLoad: ((slot: number) => void) | null = null;

  constructor(screenWidth: number, screenHeight: number) {
    this.saveManager = getSaveManager();
    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;
  }

  setScreenSize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;
  }

  open(mode: SaveMenuMode): void {
    this.mode = mode;
    this.isOpen = true;
    this.refreshSlots();
  }

  close(): void {
    this.isOpen = false;
    this.hoveredSlot = -1;
  }

  toggle(mode: SaveMenuMode): void {
    if (this.isOpen && this.mode === mode) {
      this.close();
    } else {
      this.open(mode);
    }
  }

  isVisible(): boolean {
    return this.isOpen;
  }

  setSaveCallback(callback: (slot: number) => void): void {
    this.onSave = callback;
  }

  setLoadCallback(callback: (slot: number) => void): void {
    this.onLoad = callback;
  }

  private refreshSlots(): void {
    this.slots = this.saveManager.getSaveSlots();
  }

  handleMouseMove(x: number, y: number): void {
    if (!this.isOpen) return;

    const slotHeight = 80;
    const slotWidth = 400;
    const startY = 150;
    const startX = (this.screenWidth - slotWidth) / 2;

    this.hoveredSlot = -1;

    for (let i = 0; i < this.slots.length; i++) {
      const slotY = startY + i * (slotHeight + 10);

      if (
        x >= startX &&
        x <= startX + slotWidth &&
        y >= slotY &&
        y <= slotY + slotHeight
      ) {
        this.hoveredSlot = i;
        break;
      }
    }

    // Check close button
    const closeX = this.screenWidth / 2 + 220;
    const closeY = 80;
    if (Math.abs(x - closeX) < 20 && Math.abs(y - closeY) < 20) {
      this.hoveredSlot = -2; // Special value for close button
    }
  }

  handleClick(x: number, y: number): void {
    if (!this.isOpen) return;

    // Check close button first
    const closeX = this.screenWidth / 2 + 220;
    const closeY = 80;
    if (Math.abs(x - closeX) < 20 && Math.abs(y - closeY) < 20) {
      this.close();
      return;
    }

    if (this.hoveredSlot >= 0 && this.hoveredSlot < this.slots.length) {
      const slot = this.slots[this.hoveredSlot];

      if (this.mode === 'save') {
        // Can always save to any slot
        this.onSave?.(this.hoveredSlot);
        this.refreshSlots();
      } else if (this.mode === 'load') {
        // Can only load from non-empty slots
        if (!slot.isEmpty) {
          this.onLoad?.(this.hoveredSlot);
          this.close();
        }
      }
    }
  }

  render(renderer: Renderer): void {
    if (!this.isOpen) return;

    const ctx = renderer.context;

    // Dark overlay
    ctx.fillStyle = 'rgba(0, 0, 0, 0.85)';
    ctx.fillRect(0, 0, this.screenWidth, this.screenHeight);

    // Title
    ctx.fillStyle = '#ffd700';
    ctx.font = 'bold 28px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(
      this.mode === 'save' ? t('saveMenu.saveGame') : t('saveMenu.loadGame'),
      this.screenWidth / 2,
      80
    );

    // Close button
    const closeX = this.screenWidth / 2 + 220;
    const closeY = 80;
    ctx.fillStyle = this.hoveredSlot === -2 ? '#ff6666' : '#aa4444';
    ctx.font = 'bold 24px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('X', closeX, closeY + 8);

    // Render save slots
    const slotHeight = 80;
    const slotWidth = 400;
    const startY = 150;
    const startX = (this.screenWidth - slotWidth) / 2;

    for (let i = 0; i < this.slots.length; i++) {
      const slot = this.slots[i];
      const slotY = startY + i * (slotHeight + 10);
      const isHovered = this.hoveredSlot === i;
      const isDisabled = this.mode === 'load' && slot.isEmpty;

      // Slot background
      ctx.fillStyle = isHovered && !isDisabled
        ? 'rgba(60, 60, 80, 0.9)'
        : 'rgba(30, 30, 40, 0.9)';
      ctx.fillRect(startX, slotY, slotWidth, slotHeight);

      // Slot border
      ctx.strokeStyle = isHovered && !isDisabled ? '#ffd700' : '#444466';
      ctx.lineWidth = 2;
      ctx.strokeRect(startX, slotY, slotWidth, slotHeight);

      // Slot number
      ctx.fillStyle = '#888888';
      ctx.font = 'bold 14px Arial';
      ctx.textAlign = 'left';
      ctx.fillText(`Slot ${i + 1}`, startX + 15, slotY + 25);

      if (slot.isEmpty) {
        // Empty slot
        ctx.fillStyle = isDisabled ? '#444444' : '#666666';
        ctx.font = '16px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(t('saveMenu.emptySlot'), startX + slotWidth / 2, slotY + 50);
      } else {
        // Slot info
        const classNames: Record<ClassName, string> = {
          alp: 'Alp',
          kam: 'Kam',
          mergen: 'Mergen',
          boru: 'Boru',
        };

        // Class and level
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 16px Arial';
        ctx.textAlign = 'left';
        ctx.fillText(
          `${classNames[slot.className!]} - Seviye ${slot.playerLevel}`,
          startX + 15,
          slotY + 50
        );

        // Play time
        if (slot.playTime !== undefined) {
          const playTimeStr = this.saveManager.formatPlayTime(slot.playTime);
          ctx.textAlign = 'right';
          ctx.fillText(t('saveMenu.playTime', { time: playTimeStr }), startX + slotWidth - 15, slotY + 50);
        }

        // Save date
        if (slot.timestamp) {
          const date = new Date(slot.timestamp);
          const dateStr = date.toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          });
          ctx.fillText(dateStr, startX + slotWidth - 15, slotY + 70);
        }
      }
    }

    // Instructions
    ctx.fillStyle = '#666666';
    ctx.font = '14px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(
      this.mode === 'save'
        ? t('saveMenu.selectSave')
        : t('saveMenu.selectLoad'),
      this.screenWidth / 2,
      this.screenHeight - 40
    );

    ctx.fillText(
      t('saveMenu.closeHint'),
      this.screenWidth / 2,
      this.screenHeight - 20
    );

    ctx.textAlign = 'left';
  }
}
